import { useRSOUser, deleteRSOUserApi } from "@/api/services/admin/rso/user";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { TopPage } from "@/components/elements/TopPage";
import { UpdatePartnerUserForm } from "@/features/rso/components/UpdatePartnerUserForm";
import { convertPartnerUserResToForm } from "@/features/rso/utils/converts";
import { AppRoutes } from "@/routes/config";
import { isNumber } from "@/utils/number";
import { getDynamicRoute } from "@/utils/url";
import { Box, Stack } from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { styled } from "@mui/system";
import { Button } from "@/components/elements/Button";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { useDialog } from "@/hooks/useDialog";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { useToast } from "@/hooks/useToast";
import { showError } from "@/utils/error";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useQueryClient } from "@tanstack/react-query";
import { IconDelete } from "@/assets/icons";

type TPartnerUserEditPageProps = {};

export type UpdatePartnerUserFormRef = {
  submit: () => Promise<void>;
};

export const PartnerUserEditPage: FC<TPartnerUserEditPageProps> = () => {
  const formRef = useRef<UpdatePartnerUserFormRef>(null);
  const { id, userId } = useParams();
  const navigate = useNavigate();
  const { data: rsoUserResult, isLoading, isError } = useRSOUser();
  const rsoUserData = rsoUserResult?.data;
  const confirmDelete = useDialog();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    try {
      if (!id) throw new Error();
      if (!userId) throw new Error();
      const agreeToDelete = await confirmDelete.show();
      if (!agreeToDelete) return;
      const rs = await deleteRSOUserApi(Number(id), [Number(userId)]);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RSO.FETCH_USER_LIST] });
      setIsDeleting(true);
    } catch (error) {
      showError(error);
    }
  };

  useEffect(() => {
    if (isDeleting && id) {
      navigate(getDynamicRoute(AppRoutes.rsoDetail, { id }));
    }
  }, [isDeleting, id, navigate]);

  useEffect(() => {
    if (!id || !isNumber(id) || !userId || !isNumber(userId)) {
      if (id && isNumber(id)) {
        navigate(getDynamicRoute(AppRoutes.rsoDetail, { id }), { replace: true });
      } else {
        navigate(AppRoutes.duringContractList, { replace: true });
      }
    }
  }, [id, userId, navigate]);

  useEffect(() => {
    if (id && isNumber(id) && userId && isNumber(userId) && !isLoading && (isError || !rsoUserData)) {
      if (id && isNumber(id)) {
        navigate(getDynamicRoute(AppRoutes.rsoDetail, { id }), { replace: true });
      } else {
        navigate(AppRoutes.duringContractList, { replace: true });
      }
    }
  }, [id, userId, isLoading, isError, rsoUserData, navigate]);

  if (isLoading || !rsoUserData) return <LoadingPage />;

  return (
    <PartnerWrapper sx={{ mt: 1 }}>
      <Box sx={{ mb: 2.5 }}>
        <Stack gap={4}>
          <TopPage
            enablePageBack={false}
            title="顧客ユーザーの編集"
            description="顧客側画面にログインできるユーザーアカウントに関する情報を編集できます。"
            rightContent={
              <Button variant="danger" size="sm" startIcon={<IconDelete />} onClick={handleDelete}>
                削除
              </Button>
            }
          />
          <UpdatePartnerUserForm
            previousData={convertPartnerUserResToForm(rsoUserData)}
            userData={{
              cognitoStatus: rsoUserData?.cognitoStatus,
              activeStatus: rsoUserData?.activeStatus,
            }}
            ref={formRef}
            forceDisableBlocker={isDeleting}
          />
          <ConfirmDeleteModal
            open={confirmDelete.open}
            onClose={confirmDelete.cancel}
            onOk={confirmDelete.confirm}
            message="企業ユーザーを削除してもよろしいですか？"
          />
        </Stack>
      </Box>
    </PartnerWrapper>
  );
};

const PartnerWrapper = styled(Stack)`
  width: 1198px;
  margin: 0 auto;
`;
